/* eslint-disable no-console */
import { cn } from '@/utils/cn';
import { XIcon } from 'lucide-react';
import React, { memo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { handleDomBubbleEvent } from '@/utils/dom';

export const MODAL_ROOT_Z_INDEX = 999;

interface IModalProps {
  isOpen?: boolean;
  children: React.ReactNode;
  onClose?: () => void;
  isCloseOutside?: boolean;
  onClickOutside?: () => void;
  isButtonClose?: boolean;
  className?: string;
  closeButtonClassName?: string;

  lockBodyScroll?: boolean;

  zIndex?: number;
}

function Modal({
  isOpen,
  children,
  onClose,
  isCloseOutside = true,
  onClickOutside,
  isButtonClose = true,
  className,
  closeButtonClassName,
  lockBodyScroll = true,

  zIndex = MODAL_ROOT_Z_INDEX,
}: IModalProps) {
  // HOOKS
  const modalRootRef = useRef<HTMLElement | null>(null);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!isOpen || !lockBodyScroll) return;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyPaddingRight = document.body.style.paddingRight;
    const originalBodyPosition = document.body.style.position;
    const originalBodyTop = document.body.style.top;
    const originalBodyWidth = document.body.style.width;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    // Get current scroll position
    const scrollY = window.scrollY;

    // Prevent body scroll - use position fixed for more reliable locking
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.documentElement.style.overflow = 'hidden';

    // Prevent layout shift from scrollbar
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      // Restore original styles
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.paddingRight = originalBodyPaddingRight;
      document.body.style.position = originalBodyPosition;
      document.body.style.top = originalBodyTop;
      document.body.style.width = originalBodyWidth;
      document.documentElement.style.overflow = originalHtmlOverflow;

      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, [isOpen, lockBodyScroll]);

  // Get portal root element with error handling
  if (!modalRootRef.current) {
    modalRootRef.current = document.getElementById('modal-root');
  }

  // Early return if portal root doesn't exist
  if (!modalRootRef.current) {
    console.warn('Modal root element not found');
    return null;
  }

  // RENDER
  if (!isOpen) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      style={{
        zIndex,
      }}
      className="fixed inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm transition-opacity duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClickOutside?.();
          if (isCloseOutside) {
            onClose?.();
          }
        }
      }}
    >
      <div
        tabIndex={-1}
        className={cn(
          'p-[24px] rounded-[16px] relative border border-[#D9D9D9] bg-[#FFF] w-[500px] max-h-[90vh] flex flex-col',
          'transform transition-all duration-200',
          'focus:outline-none',
          className
        )}
        style={{
          boxShadow: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {isButtonClose ? (
          <button
            type="button"
            aria-label="Close modal"
            onClick={(e) => {
              handleDomBubbleEvent(e);
              onClose?.();
            }}
            className={cn(
              'absolute top-[16px] right-[16px] bg-[#F5F5F5] rounded-full p-[4px] flex items-center justify-center z-10 cursor-pointer',
              'hover:bg-[#E5E5E5] transition-colors duration-150',
              'focus:outline-none',
              closeButtonClassName
            )}
          >
            <XIcon className="text-[#0C8DBF] w-[16px] h-[16px] flex-shrink-0" aria-hidden="true" />
          </button>
        ) : null}

        {children}
      </div>
    </div>,
    modalRootRef.current
  );
}

// Memo with custom comparison to prevent unnecessary re-renders
export default memo(Modal);
